import i18n from "./i18n/config";
import { getGameplayToken } from "./auth";

export interface ScoreEntry {
  player_id?: string;
  player_name: string;
  player_provider?: string | null;
  player_image?: string | null;
  completion_time_seconds: number;
  found_count: number;
  target_count: number;
  moves: Array<{
    title: string;
    duration_seconds: number;
    is_target: boolean;
  }>;
}

export interface ScoreboardResponse {
  date: string;
  language: string;
  scores: ScoreEntry[];
}

async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getGameplayToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

export const api = {
  async createDailyChallenge(language = "en"): Promise<string> {
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await authenticatedFetch("/api/daily", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async createLobby(language = "en"): Promise<string> {
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await authenticatedFetch("/api/lobby/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async getScoreboard(
    language = "en",
    daysAgo = 0,
  ): Promise<ScoreboardResponse> {
    const response = await authenticatedFetch(
      `/api/daily/scoreboard?language=${language}&days_ago=${daysAgo}`,
      { credentials: "include" },
    );

    if (!response.ok) {
      throw new Error(i18n.t("errors.scoreboardFailed"));
    }

    return response.json();
  },

  async getStreamer(): Promise<string | null> {
    const response = await fetch("/streamer");

    if (!response.ok) {
      return null;
    }

    const streamer = (await response.text()).trim();
    return streamer || null;
  },
};
